import { ImageWithFallback } from './image/ImageWithFallback';
import { ExternalLink, Sparkles, Layers, Palette } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';
import { Badge } from './ui/Badge';
import { motion, useInView, useScroll, useTransform, useSpring, useReducedMotion } from 'motion/react';
import { useMemo, useRef } from 'react';

export function Projects() {
    const ref = useRef(null);
    const reduceMotion = useReducedMotion();
    const isInView = useInView(ref, { once: true, margin: '-120px' });

    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ['start end', 'end start'],
    });

    const y1Raw = useTransform(scrollYProgress, [0, 1], ['0%', '28%']);
    const y2Raw = useTransform(scrollYProgress, [0, 1], ['0%', '-28%']);
    const y1 = useSpring(y1Raw, { stiffness: 85, damping: 22 });
    const y2 = useSpring(y2Raw, { stiffness: 85, damping: 22 });

    const projects = useMemo(
        () => [
            {
                title: 'Domovia CRM / Berater+',
                subtitle: 'Branding & UI-System',
                description:
                    'Unternehmensanwendung (CRM) für freie Versicherungsmakler. Ich sorge für das Look & Feel – von der Unternehmenswebsite bis zur Oberfläche des CRM-Systems.',
                image:
                    'assets/projects/webappkunden.png',
                tags: ['UI/UX', 'Design System', 'Branding'],
                icon: Palette,
                highlight: 'Look & Feel',
            },
            {
                title: 'LaunchUI',
                subtitle: 'Plattform (im Aufbau)',
                description:
                    'Eine Plattform, die ich gerade gründe: Landingpages designen & teilen, den eigenen Tech-Stack repräsentieren und Inspiration finden – alles an einem Ort.',
                image:
                    'https://images.unsplash.com/photo-1555066931-4365d14bab8c?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcm9kdWN0JTIwZGVzaWduJTIwdWl8ZW58MXx8fHwxNzY3Mjc1Mjg0fDA&ixlib=rb-4.1.0&q=80&w=1400&utm_source=figma&utm_medium=referral',
                tags: ['Community', 'Landing Pages', 'Tech Stack'],
                icon: Layers,
                highlight: 'Build in public',
            },
        ],
        []
    );

    return (
        <section id="projekte" className="relative py-24 px-4 overflow-hidden" ref={ref}>
            {/* Background */}
            <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-white" />

            {/* Ambient blobs */}
            <motion.div
                className="absolute top-28 right-[-120px] w-[520px] h-[520px] rounded-full blur-3xl opacity-25"
                style={{
                    background: 'radial-gradient(circle, rgba(249,115,22,0.32), transparent 60%)',
                    y: y1,
                }}
                animate={reduceMotion ? undefined : { scale: [1, 1.08, 1], opacity: [0.18, 0.3, 0.18] }}
                transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
                aria-hidden="true"
            />
            <motion.div
                className="absolute bottom-[-140px] left-[-140px] w-[560px] h-[560px] rounded-full blur-3xl opacity-25"
                style={{
                    background: 'radial-gradient(circle, rgba(59,130,246,0.32), transparent 60%)',
                    y: y2,
                }}
                animate={reduceMotion ? undefined : { scale: [1, 1.1, 1], opacity: [0.16, 0.28, 0.16] }}
                transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
                aria-hidden="true"
            />

            {/* Subtle grid */}
            <div
                className="absolute inset-0 opacity-[0.06]"
                style={{
                    backgroundImage:
                        'linear-gradient(rgba(0,0,0,0.22) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.22) 1px, transparent 1px)',
                    backgroundSize: '64px 64px',
                    maskImage: 'radial-gradient(circle at 50% 30%, black, transparent 70%)',
                }}
                aria-hidden="true"
            />

            <div className="relative z-10 max-w-7xl mx-auto">
                {/* Header */}
                <motion.div
                    className="text-center mb-14"
                    initial={{ opacity: 0, y: 26 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.7, ease: 'easeOut' }}
                >
                    <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-border/60 bg-white/70 backdrop-blur px-4 py-2 shadow-sm">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-pink-600 text-white">
              <Sparkles className="h-4 w-4" />
            </span>
                        <span className="text-sm text-gray-700">Meine Projekte</span>
                        <span className="ml-1 rounded-full bg-black/5 px-2 py-0.5 text-xs text-gray-700">
              Ausgewählte Highlights
            </span>
                    </div>

                    <h2 className="mt-6 text-5xl md:text-6xl tracking-tight">
            <span className="bg-gradient-to-r from-orange-500 to-pink-600 bg-clip-text text-transparent">
              Projekte
            </span>{' '}
                        <span className="text-gray-900">mit Fokus auf Ergebnis & Qualität</span>
                    </h2>

                    <p className="mt-5 text-xl text-gray-600 max-w-2xl mx-auto">
                        Zwei Projekte, auf die ich besonders stolz bin – vom CRM-Look-&-Feel bis zur neuen Plattformidee.
                    </p>
                </motion.div>

                {/* Layout: 2 big feature cards */}
                <div className="grid lg:grid-cols-2 gap-8">
                    {projects.map((project, index) => {
                        const Icon = project.icon;
                        return (
                            <motion.div
                                key={project.title}
                                initial={{ opacity: 0, y: 34 }}
                                animate={isInView ? { opacity: 1, y: 0 } : {}}
                                transition={{ duration: 0.6, delay: 0.1 * index }}
                                whileHover={{ y: -8 }}
                            >
                                <Card className="group relative overflow-hidden h-full rounded-2xl border border-gray-200/70 bg-white/75 backdrop-blur shadow-lg hover:shadow-2xl transition-shadow">
                                    {/* Gradient glow */}
                                    <div className="pointer-events-none absolute -inset-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-xl bg-gradient-to-r from-orange-500/20 via-pink-500/20 to-blue-500/20" />

                                    {/* Image */}
                                    <div className="relative h-60 overflow-hidden">
                                        <ImageWithFallback
                                            src={project.image}
                                            alt={project.title}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

                                        {/* Top right action */}
                                        <motion.div
                                            className="absolute top-4 right-4 w-11 h-11 bg-white/95 rounded-full flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                                            whileHover={{ scale: 1.08 }}
                                        >
                                            <ExternalLink className="w-5 h-5 text-gray-900" />
                                        </motion.div>

                                        {/* Bottom left label */}
                                        <div className="absolute bottom-4 left-4 flex items-center gap-2">
                                            <div className="h-10 w-10 rounded-xl bg-white/90 flex items-center justify-center shadow-md">
                                                <Icon className="h-5 w-5 text-gray-900" />
                                            </div>
                                            <div className="rounded-xl bg-white/15 backdrop-blur px-4 py-2 border border-white/20">
                                                <div className="text-xs text-white/80">{project.subtitle}</div>
                                                <div className="text-sm text-white font-medium">{project.highlight}</div>
                                            </div>
                                        </div>
                                    </div>

                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-2xl">{project.title}</CardTitle>
                                        <CardDescription className="text-base leading-relaxed">
                                            {project.description}
                                        </CardDescription>
                                    </CardHeader>

                                    <CardContent>
                                        <div className="flex flex-wrap gap-2">
                                            {project.tags.map((tag) => (
                                                <Badge key={tag} variant="secondary">
                                                    {tag}
                                                </Badge>
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
